import React, { useCallback, useEffect, useState } from "react"
import { RootState, useTypedSelector } from "../app/store"
import { environment } from "../environments/environment"
import { Simulation } from "../features/simulation/types"
import * as utils from "../features/simulation/utils"
import {
  convertToSeconds,
  convertToTime,
  processSimulations,
} from "../features/simulation/utils"
import { dangerousInnerHtmlObj } from "../utils/sanitizer"
import DevMenu from "./dev-menu"
import HelpModal from "./help-modal"
import Modal from "./modal"
import BaseButton from "./Button/base-button"

type SimulationHeaderProps = {
  setModalBreaktimeOpen: (isOpen: boolean) => void
  simulation: Simulation[]
}

const SimulationHeader: React.FC<SimulationHeaderProps> = ({
  setModalBreaktimeOpen,
  simulation,
}) => {
  const [isModalSimulationOpen, setModalSimulationOpen] = React.useState(false)
  const [isHelpModalOpen, setHelpModalOpen] = React.useState(false)
  const [countdownTime, setCountdownTime] = React.useState("")
  const [simulations, setSimulations] = useState<Simulation[]>([])
  const simulationStore = useTypedSelector((state: RootState) => {
    return state.simulationSlice
  })

  const elapsedTime = useTypedSelector((state: RootState) => {
    return convertToTime(state.simulationSlice.secondsElapsed)
  })

  const { timerAsCountdown, duration } = useTypedSelector(
    (state: RootState) => {
      return state.simulationSlice
    },
  )

  const openModalBreaktime = useCallback(() => {
    setModalBreaktimeOpen(true)
  }, [setModalBreaktimeOpen])

  useEffect(() => {
    if (timerAsCountdown) {
      const durationSeconds = convertToSeconds(duration)
      const timeLeft = durationSeconds - simulationStore.secondsElapsed
      setCountdownTime(convertToTime(timeLeft))
    }
  }, [timerAsCountdown, elapsedTime])
  useEffect(() => {
    if (simulation && simulation.length > 0) {
      const sim_data = processSimulations(simulation)
      setSimulations(sim_data as Simulation[])
    }
  }, [simulation])

  return (
    <div
      id="simulationHeader"
      className="self-stretch flex flex-row items-center justify-between">
      {simulations[0]?.imageData && simulations[0].imageData.startsWith("<svg") ? (
        <div
          className="relative w-[229px] h-12 overflow-hidden shrink-0 flex items-center"
          dangerouslySetInnerHTML={{ __html: simulations[0].imageData }}
        />
      ) : (
        <img
          className="relative w-[229px] h-12 object-contain shrink-0"
          alt=""
          src={
            utils.getLocalSimLogo(simulations[0]?.shortName) ||
            simulations[0]?.imageData ||
            simulations[0]?.imgUrl ||
            environment.fallback_img_url
          }
          onError={(e) => {
            e.currentTarget.src = environment.fallback_img_url // Fallback image path
          }}
        />
      )}
      <div className="h-10 flex flex-row items-start justify-start gap-[10px]">
        <DevMenu />
        <BaseButton
          id="pauseButton"
          className="w-[105px] rounded-lg text-sm overflow-hidden justify-right"
          onClick={openModalBreaktime}
          title="Pause Simulation">
          <img
            className="absolute left-2 w-5 h-5 overflow-hidden shrink-0"
            alt="Pause Simulation"
            src="/v3/assets/pause-circle.svg"
          />
          <div className="ml-7 mr-2 w-12 relative leading-[20px] font-semibold">
            {timerAsCountdown ? countdownTime : elapsedTime}
          </div>
        </BaseButton>
        <BaseButton
          id="aboutSimulationButton"
          variant="secondary"
          onClick={() => setModalSimulationOpen(true)}
          className="rounded-lg text-sm overflow-hidden">
          <div className="relative leading-[20px] font-semibold">
            About the Simulation
          </div>
        </BaseButton>
        <Modal
          id="aboutSimulationModal"
          title="About the Simulation"
          open={isModalSimulationOpen}
          okButton={{
            name: "Close",
            onClick: () => setModalSimulationOpen(false),
          }}
          onClose={() => setModalSimulationOpen(false)}>
          <div
            className="self-stretch relative text-sm leading-[24px] text-gray-600"
            dangerouslySetInnerHTML={dangerousInnerHtmlObj(
              simulationStore.simulationAboutText,
            )}
          />
        </Modal>
        <BaseButton
          id="helpButton"
          variant="secondary"
          onClick={() => setHelpModalOpen(true)}
          className="rounded-lg text-sm overflow-hidden">
          <div className="relative leading-[20px] font-semibold mr-2">Help</div>
          <img
            className="relative w-5 h-5 overflow-hidden shrink-0 cursor-help"
            alt=""
            src="/v3/assets/help-circle.svg"
            title="If you have questions regarding the interface or are facing any technical challenges, please directly contact our support team. Click on help to launch a chat window with our support team."
          />
        </BaseButton>
        <HelpModal
          isHelpModalOpen={isHelpModalOpen}
          setHelpModalOpen={setHelpModalOpen}
        />
      </div>
    </div>
  )
}

export default SimulationHeader
